import { FetchResult } from '@apollo/client';
import { FormErrorMessage } from '@pendwa/common-browser/components/Form/ErrorMessage/index.js';
import { LayoutSpacerSmall } from '@pendwa/common-browser/components/LayoutSpacerSmall/index.js';
import { LoadingSmall } from '@pendwa/common-browser/components/Loading/LoadingSmall/index.js';
import { extractGraphQLErrorMessage } from '@pendwa/common-browser/core/apollo/utils.js';
import { logger } from '@pendwa/common-browser/core/logger/index.js';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { facebookSDKLogin, isValidFacebookLoginRedirect } from '../../../core/facebook/sdk.js';
import { HooksAccountLoginFacebookMutation } from '../../../generated/graphql.js';
import { useAccountLoginFacebookGraphql } from '../../../hooks/user/loginFacebookGraphql.js';
import { SignInWithFacebookButton } from './components/SignInWithFacebookButton/index.js';

export const LoginFacebook: React.FC<{
  button_text: string;
  handleSuccess: (result: FetchResult<HooksAccountLoginFacebookMutation>) => void;
}> = (props) => {
  const [frontend_error_message, setFrontendErrorMessage] = React.useState<string | undefined>('');
  const location = useLocation();
  const { result: mutation_result, doAction } = useAccountLoginFacebookGraphql();

  const onFail = (error: any) => {
    logger.error(
      new Error('fail to initialize facebook button', {
        cause: error,
      })
    );
    setFrontendErrorMessage('Failed to contact Facebook');
  };

  const executeAction = async ({ access_token }: { access_token: string }) => {
    setFrontendErrorMessage(undefined);

    const action_result = await doAction({
      variables: {
        body: {
          access_token,
        },
      },
    });

    if (!action_result.errors && action_result.data?.accountLoginFacebook?.data?.uuid) {
      setTimeout(() => props.handleSuccess(action_result), 500);
    }
  };

  // using redirect, facebook returns the access_token in the url search
  React.useEffect(() => {
    const location_hash = location.hash;
    if (location_hash) {
      try {
        const { access_token, state } = location_hash
          .replace(/^#/, '')
          .split('&')
          .map((v) => v.split('='))
          .reduce((pre, [key, value]) => ({ ...pre, [key]: value }), {}) as {
          access_token: string | undefined;
          state: string | undefined;
        };
        if (access_token && isValidFacebookLoginRedirect(state || '')) {
          executeAction({ access_token });
        }
      } catch (error) {
        onFail(new Error(`failed to parse location hash ${location_hash}`));
      }
    }
  }, []);

  return (
    <LayoutSpacerSmall>
      {frontend_error_message && <FormErrorMessage message={frontend_error_message} />}

      {mutation_result.error && (
        <FormErrorMessage
          message={extractGraphQLErrorMessage(mutation_result.error) ?? 'Something went wrong.'}
        />
      )}

      <SignInWithFacebookButton
        text={props.button_text}
        loading={mutation_result.loading}
        onClick={() =>
          facebookSDKLogin().catch((error) => {
            onFail(error);
          })
        }
      />

      {mutation_result.loading && <LoadingSmall />}
    </LayoutSpacerSmall>
  );
};
